import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { gsap, ScrollTrigger } from "../lib/gsap";
import Breadcrumbs from "../components/site/Breadcrumbs";

const PHILOSOPHY = [
  { n: "01", t: "Significant Minority Shareholding", d: "Active stewardship, not passive ownership. We influence governance, optimise capital structures, and open new market access — without the operational burden of day-to-day management." },
  { n: "02", t: "Owning the Means of Production", d: "Tangible assets that generate utility — logistics infrastructure, telecoms networks, mining assets — the productive backbone of African economic development." },
  { n: "03", t: "Risk-Adjusted Returns", d: "A rigorous, data-driven investment committee. Comprehensive financial, legal, and environmental due diligence. Predictable cash flows. Strong barriers to entry." },
  { n: "04", t: "ESG Integration", d: "Environmental, social and governance criteria woven through every investment and advisory mandate. Responsibility as both a moral and a financial imperative." },
];

const SECTORS = [
  { t: "Logistics", d: "Port logistics, warehousing and last-mile delivery solutions that bridge the infrastructure gap in key trade corridors.", img: "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?w=1200&q=80&auto=format&fit=crop" },
  { t: "Property & Real Estate", d: "Premium, well-managed property and mixed-use developments in high-growth nodes — a hedge against inflation, generator of durable income.", img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80&auto=format&fit=crop" },
  { t: "Telecom & Fintech", d: "Digital transformation of Africa via telecoms infrastructure and mobile financial services — expanding financial inclusion.", img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80&auto=format&fit=crop" },
  { t: "Financial Services", d: "Capital and advisory to banks, insurers and alternative lenders — risk management, regulatory capacity, SME lending expansion.", img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80&auto=format&fit=crop" },
  { t: "Mining & Critical Minerals", d: "Mines with large reserves, low production costs and strategic relevance to global green-energy supply chains.", img: "https://images.unsplash.com/photo-1523848309072-c199db53f137?w=1200&q=80&auto=format&fit=crop" },
  { t: "Infrastructure Development", d: "Large-scale civil, urban and social-infrastructure projects — from transport corridors and water utilities to housing precincts and public-private partnerships.", img: "https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/nk4z2m9j_94e38f4a-4279-4e18-8ade-0fb9fd60153b.png" },
  { t: "Renewable Energy", d: "Utility-scale solar, wind and battery-storage projects across Southern and Sub-Saharan Africa — supporting the continent's just energy transition.", img: "https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/7r2w3g2l_ed7ec24d-a0c8-4df0-94e0-79dc61bde7e1.png" },
];

export default function About() {
  const root = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray(".js-reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { y: 60, opacity: 0 },
          { y: 0, opacity: 1, duration: 1.1, ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 85%" } }
        );
      });

      // Sticky philosophy section subtle parallax line
      gsap.utils.toArray("[data-parallax]").forEach((el) => {
        gsap.to(el, {
          yPercent: -12,
          ease: "none",
          scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: true },
        });
      });
    }, root);

    return () => {
      ctx.revert();
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  return (
    <div ref={root} data-testid="about-page">
      {/* HERO */}
      <section className="relative pt-0 pb-24 md:pb-32 min-h-[80vh] md:min-h-[90vh] overflow-hidden flex items-end">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/w9z6cojx_1.jpg"
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: "saturate(1) brightness(0.85) contrast(1.05)", objectPosition: "center" }}
          />
          {/* Left-side dark wash so headline reads */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(5,9,20,0.92) 0%, rgba(5,9,20,0.78) 30%, rgba(5,9,20,0.45) 60%, rgba(5,9,20,0.20) 100%)" }} />
          {/* Top fade to support the navbar */}
          <div className="absolute inset-x-0 top-0 h-48" style={{ background: "linear-gradient(180deg, rgba(5,9,20,0.65) 0%, transparent 100%)" }} />
          {/* Bottom fade into bg */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 0%, transparent 70%, rgba(5,9,20,0.50) 90%, var(--bg) 100%)" }} />
        </div>
        <div className="container-x relative z-10 w-full">
          <Breadcrumbs />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mt-12 md:mt-20">
            <div className="md:col-span-8 js-reveal">
              <span className="overline" style={{ color: "#FDFBF7" }}>— About GICP</span>
              <h1 className="font-serif text-5xl md:text-7xl lg:text-[6.5rem] mt-6 leading-[0.92] tracking-tighter" style={{ color: "#FDFBF7" }}>
                Intelligent <em className="italic" style={{ color: "var(--gicp-gold)" }}>capital.</em><br/>
                Execution-driven<br/>
                advisory.
              </h1>
              <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "rgba(253,251,247,0.80)" }}>
                A premier investment holding and corporate finance advisory firm — partnering
                with the institutions, founders and management teams shaping the African century.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EXECUTIVE OVERVIEW */}
      <section className="section" style={{ borderTop: "1px solid var(--border-subtle)" }}>
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 js-reveal">
            <span className="overline">— Executive Overview</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-6 leading-[0.95] tracking-tight">
              A bridge between<br/>institutional capital<br/>and high-growth<br/><em className="italic" style={{ color: "var(--gicp-gold)" }}>opportunities.</em>
            </h2>
          </div>
          <div className="md:col-span-7 js-reveal space-y-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--fg-soft)" }}>
            <p>
              Global Investment Capital Partners (GICP) is a premier investment holding and corporate finance advisory firm
              with a deep-rooted presence in the Southern African market and a strategic lens focused on the broader continent.
            </p>
            <p>
              We distinguish ourselves through a dual mandate: intelligent capital deployment and execution-driven advisory.
              We do not simply provide advice — we invest alongside our partners, aligning our financial interests with our stakeholders.
            </p>
            <p>
              Headquartered at the Menlyn Maine precinct in Pretoria, our operations extend across the SADC region
              and into key African markets through a strategic partnership model.
            </p>
            <div className="pt-4 grid grid-cols-3 gap-6 border-t" style={{ borderColor: "var(--border-subtle)" }}>
              <div className="pt-6">
                <div className="overline">Headquarters</div>
                <p className="mt-3 text-sm" style={{ color: "var(--fg)" }}>Menlyn Maine, Pretoria</p>
              </div>
              <div className="pt-6">
                <div className="overline">Footprint</div>
                <p className="mt-3 text-sm" style={{ color: "var(--fg)" }}>SADC & Pan-African</p>
              </div>
              <div className="pt-6">
                <div className="overline">Mandate</div>
                <p className="mt-3 text-sm" style={{ color: "var(--fg)" }}>Capital + Advisory</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARALLAX IMAGE INTERLUDE */}
      <section className="relative h-[55vh] md:h-[70vh] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/eq6sad2g_AdobeStock_1974686400.jpeg"
          alt="Executives in boardroom"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "saturate(1) brightness(0.65) contrast(1.05)" }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, var(--bg) 0%, transparent 30%, transparent 70%, var(--bg) 100%)" }} />
        <div className="absolute inset-0 flex items-center">
          <div className="container-x">
            <div className="max-w-3xl js-reveal">
              <span className="overline" style={{ color: "#FDFBF7" }}>— Stewardship</span>
              <p className="font-serif italic text-3xl md:text-5xl mt-6 leading-[1.1]" style={{ color: "#FDFBF7" }}>
                &ldquo;We partner with management teams who are leaders in their respective fields — without assuming the operational burden.&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="section" style={{ background: "var(--bg-soft)" }}>
        <div className="container-x">
          <div className="js-reveal max-w-3xl mb-16">
            <span className="overline">— Investment Philosophy</span>
            <h2 className="font-serif text-4xl md:text-6xl mt-6 leading-[0.95] tracking-tight">
              Long-term value.<br/>
              <em className="italic" style={{ color: "var(--gicp-gold)" }}>Patient</em> stewardship.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ background: "var(--border-subtle)" }}>
            {PHILOSOPHY.map((p) => (
              <div key={p.n} className="p-10 md:p-14 js-reveal" style={{ background: "var(--bg-soft)" }}>
                <div className="text-xs tracking-[0.3em]" style={{ color: "var(--gicp-gold)" }}>{p.n} —</div>
                <h3 className="mt-6 font-serif text-3xl md:text-4xl leading-tight">{p.t}</h3>
                <p className="mt-6 text-sm md:text-base leading-relaxed" style={{ color: "var(--fg-soft)" }}>{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTOR FOCUS */}
      <section className="section">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-5 js-reveal">
              <span className="overline">— Sector Focus</span>
              <h2 className="font-serif text-4xl md:text-6xl mt-6 leading-[0.95] tracking-tight">
                Diversified.<br/>
                <em className="italic" style={{ color: "var(--gicp-gold)" }}>Selective.</em><br/>
                Strategic.
              </h2>
            </div>
            <div className="md:col-span-7 js-reveal text-base md:text-lg leading-relaxed" style={{ color: "var(--fg-soft)" }}>
              GICP deploys capital and advisory expertise across a curated portfolio of high-growth, high-resilience
              sectors fundamental to Africa&rsquo;s industrialisation and digital transformation.
            </div>
          </div>

          <div className="border-t" style={{ borderColor: "var(--border-subtle)" }}>
            {SECTORS.map((s, i) => (
              <div key={s.t} className="js-reveal grid grid-cols-1 md:grid-cols-12 gap-8 py-10 border-b group transition-colors items-center" style={{ borderColor: "var(--border-subtle)" }}>
                <div className="md:col-span-1 text-xs tracking-[0.3em] pt-3" style={{ color: "var(--gicp-gold)" }}>0{i + 1}</div>
                <div className="md:col-span-3">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={s.img}
                      alt={s.t}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                      style={{ filter: "saturate(0.85)" }}
                    />
                  </div>
                </div>
                <div className="md:col-span-3">
                  <h3 className="font-serif text-3xl md:text-4xl tracking-tight group-hover:translate-x-2 transition-transform duration-500" style={{ color: "var(--fg)" }}>{s.t}</h3>
                </div>
                <div className="md:col-span-5 text-sm md:text-base leading-relaxed" style={{ color: "var(--fg-soft)" }}>
                  {s.d}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO CASE STUDIES */}
      <section className="section" style={{ background: "var(--bg-soft)" }}>
        <div className="container-x">
          <div className="js-reveal max-w-3xl mb-16">
            <span className="overline">— Portfolio Case Studies</span>
            <h2 className="font-serif text-4xl md:text-6xl mt-6 leading-[0.95] tracking-tight">
              The continent&rsquo;s <em className="italic" style={{ color: "var(--gicp-gold)" }}>anchors</em>.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {[
              { name: "Attacq Ltd", sector: "Real Estate REIT", body: "Master developer of Waterfall City, Gauteng — one of Africa's most significant mixed-use precincts. Portfolio over R28 billion.", logo: "https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/pis5n2qi_958266_edfbc99690e1460898c6abe764d4f55e~mv2.png.avif" },
              { name: "MTN Group", sector: "Telecom · Fintech", body: "Africa's leading telecom operator, pivoting from pure-play mobile voice to a comprehensive digital ecosystem. Mobile Money is the financial-inclusion engine of the continent.", logo: "https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/7y23lxqb_channels4_profile.jpg" },
              { name: "Valterra Platinum", sector: "Mining · PGMs", body: "World's leading primary producer of Platinum Group Metals — critical catalysts for hydrogen, clean engines and fuel cells. Positioned to capture long-term value from the green-energy transition.", logo: "https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/yk644i7d_Valterra-Platinum-Logo-1024x585.png.webp" },
            ].map((c) => (
              <article key={c.name} className="js-reveal group flex flex-col items-center text-center">
                <div
                  className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden flex items-center justify-center p-8 md:p-10 transition-transform duration-700 group-hover:-translate-y-1"
                  style={{ background: "var(--gicp-bone)", border: "1px solid var(--border-subtle)" }}
                >
                  <img
                    src={c.logo}
                    alt={`${c.name} logo`}
                    className="max-h-full max-w-full object-contain"
                    draggable="false"
                  />
                </div>
                <div className="mt-8 max-w-sm">
                  <div className="overline">{c.sector}</div>
                  <h3 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight">{c.name}</h3>
                  <p className="mt-5 text-sm md:text-base leading-relaxed" style={{ color: "var(--fg-soft)" }}>{c.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-x text-center js-reveal">
          <span className="overline">— Partner with GICP</span>
          <h2 className="font-serif text-4xl md:text-7xl mt-6 leading-[0.95] tracking-tight max-w-4xl mx-auto">
            Execution focus. Capital access.<br/>
            <em className="italic" style={{ color: "var(--gicp-gold)" }}>Integrity.</em>
          </h2>
          <Link to="/contact" data-testid="about-cta" className="btn-gold mt-10 inline-flex">
            Start a conversation <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
